import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getActiveBanners, recordBannerClick } from "../../utils/banners";
import { ArrowRight, X } from "lucide-react";

export default function SlimAdBanner({ placement = "all", style = {}, className = "", dismissible = false }) {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if dismissed in this session
    try {
      const isDismissed = sessionStorage.getItem(`afriport_dismiss_banner_${placement}`);
      if (isDismissed === "true") {
        setDismissed(true);
      }
    } catch (e) {
      // Storage unavailable
    }

    let isMounted = true;
    getActiveBanners(placement).then(list => {
      if (isMounted) {
        setBanners(list);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [placement]);

  if (loading || dismissed || banners.length === 0) {
    return null;
  }

  // Pick the primary active banner (or first)
  const banner = banners[0];

  function handleClick(e) {
    if (banner.id) {
      recordBannerClick(banner.id);
    }

    if (!banner.linkUrl) return;

    if (banner.linkUrl.startsWith("http://") || banner.linkUrl.startsWith("https://")) {
      window.open(banner.linkUrl, banner.openInNewTab ? "_blank" : "_self", "noopener,noreferrer");
    } else {
      navigate(banner.linkUrl);
    }
  }

  function handleDismiss(e) {
    e.stopPropagation();
    setDismissed(true);
    try {
      sessionStorage.setItem(`afriport_dismiss_banner_${placement}`, "true");
    } catch (err) {
      // Ignore
    }
  }

  const isPureImage = banner.imageUrl && !banner.title && !banner.subtitle;

  return (
    <div
      className={`slim-ad-banner-wrap ${className}`}
      style={{
        width: "100%",
        margin: "0 auto",
        padding: placement === "global_top" ? "0" : "12px 0",
        ...style,
      }}
    >
      <div
        onClick={handleClick}
        style={{
          position: "relative",
          cursor: banner.linkUrl ? "pointer" : "default",
          borderRadius: placement === "global_top" ? 0 : 12,
          overflow: "hidden",
          boxShadow: placement === "global_top" ? "none" : "0 4px 15px rgba(0,0,0,0.06)",
          border: placement === "global_top" ? "none" : "1px solid rgba(0,0,0,0.08)",
          background: banner.backgroundColor || "linear-gradient(90deg, #0F172A 0%, #1E293B 100%)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onMouseEnter={e => {
          if (banner.linkUrl) {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.12)";
          }
        }}
        onMouseLeave={e => {
          if (banner.linkUrl) {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = placement === "global_top" ? "none" : "0 4px 15px rgba(0,0,0,0.06)";
          }
        }}
      >
        {/* Discreet Ad / Sponsored Tag */}
        <div
          style={{
            position: "absolute",
            top: 6,
            right: dismissible ? 32 : 10,
            zIndex: 3,
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(4px)",
            color: "rgba(255,255,255,0.85)",
            fontSize: 9,
            fontWeight: 700,
            padding: "2px 6px",
            borderRadius: 4,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            pointerEvents: "none",
          }}
        >
          {banner.badge || "Sponsored"}
        </div>

        {/* Dismiss Button */}
        {dismissible && (
          <button
            onClick={handleDismiss}
            style={{
              position: "absolute",
              top: 6,
              right: 8,
              zIndex: 4,
              border: "none",
              background: "rgba(0,0,0,0.6)",
              color: "#fff",
              borderRadius: "50%",
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
            }}
            title="Dismiss ad"
          >
            <X size={12} />
          </button>
        )}

        {isPureImage ? (
          /* Pure Graphic Banner Image */
          <div style={{ width: "100%", maxHeight: "clamp(60px, 11vw, 110px)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img
              src={banner.imageUrl}
              alt={banner.title || "Promotional banner"}
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                maxHeight: "clamp(60px, 11vw, 110px)",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>
        ) : (
          /* Hybrid Banner with image background/illustration + text overlay */
          <div
            style={{
              width: "100%",
              padding: "clamp(12px, 2.5vw, 18px) clamp(16px, 3.5vw, 28px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              position: "relative",
              color: banner.textColor || "#fff",
              minHeight: "clamp(64px, 9vw, 90px)",
              backgroundImage: banner.imageUrl ? `linear-gradient(rgba(15, 23, 42, 0.75), rgba(15, 23, 42, 0.75)), url(${banner.imageUrl})` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14, maxWidth: "80%" }}>
              {banner.imageUrl && !banner.imageUrl.includes("linear-gradient") && (
                <div style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}>
                  <img src={banner.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              )}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {banner.badge && (
                    <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 4, background: "var(--brand)", color: "#fff", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {banner.badge}
                    </span>
                  )}
                  <h4 style={{ margin: 0, fontSize: "clamp(14px, 2vw, 16px)", fontWeight: 700, lineHeight: 1.3 }}>
                    {banner.title}
                  </h4>
                </div>
                {banner.subtitle && (
                  <p style={{ margin: "2px 0 0", fontSize: 13, opacity: 0.85, lineHeight: 1.4 }}>
                    {banner.subtitle}
                  </p>
                )}
              </div>
            </div>

            {banner.linkUrl && (
              <div
                className="btn btn-primary btn-sm"
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "8px 16px",
                  borderRadius: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                }}
              >
                {banner.ctaText || "Explore"} <ArrowRight size={14} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
